import React, { useCallback, useContext, useMemo } from 'react'

import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import PropertyRateOption from './PropertyRateOption'
import PropertyRateVariantSelector from './PropertyRateVariantSelector'

interface Props {
  lpcOffer?: App.HotelOffer
  bedbankOffer?: App.BedbankOffer
  rates: Array<App.PropertyRate>
  defaultRateId: string
  /** The property room's real id, distinct from `roomListId` (an analytics-only grouping id). */
  roomId: string
  roomListId: string
  roomPosition: number
  rateListId: string
  optionNumber: number
  propertyTimezone: string
  currency: string
  isLoyaltyProgramFeatureFlagEnabled: boolean
  sabreHotelsEnabled: boolean
}

/**
 * Merged variant that combines interchangeable property rates with radio selection.
 * Injects the variant selector where the cancellation policy would normally go.
 */
function PropertyRateOptionMerged(props: Props) {
  const {
    lpcOffer,
    bedbankOffer,
    rates,
    defaultRateId,
    roomId,
    roomListId,
    roomPosition,
    rateListId,
    optionNumber,
    propertyTimezone,
    currency,
    isLoyaltyProgramFeatureFlagEnabled,
    sabreHotelsEnabled,
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const storedRateId = useContext(OfferPageStateContext).viewedRateIdByRoomId?.[roomId]

  const selectedRateId = useMemo(() => {
    if (storedRateId && rates.some(rate => rate.rateId === storedRateId)) {
      return storedRateId
    }
    return defaultRateId
  }, [defaultRateId, rates, storedRateId])

  const setSelectedRateId = useCallback((rateId: string) => {
    offerPageDispatch({ type: OfferPageActions.setViewedRoomRateId, roomId, rateId })
  }, [offerPageDispatch, roomId])

  const activeRate = useMemo(() => {
    return rates.find(rate => rate.rateId === selectedRateId) ?? rates[0]
  }, [rates, selectedRateId])

  const hasChoice = rates.length > 1

  if (!activeRate) {
    return null
  }

  const variantSelectorContentOverride =
    hasChoice ?
        <PropertyRateVariantSelector
          rates={rates}
          selectedRateId={selectedRateId}
          onChange={setSelectedRateId}
          propertyTimezone={propertyTimezone}
          currency={currency}
          isLoyaltyProgramFeatureFlagEnabled={isLoyaltyProgramFeatureFlagEnabled}
          sabreHotelsEnabled={sabreHotelsEnabled}
        /> :
      undefined

  return <PropertyRateOption
    lpcOffer={lpcOffer}
    bedbankOffer={bedbankOffer}
    rate={activeRate}
    optionNumber={optionNumber}
    roomListId={roomListId}
    roomPosition={roomPosition}
    rateListId={rateListId}
    loyaltySelectorContentOverride={variantSelectorContentOverride}
  />
}

export default PropertyRateOptionMerged
